import type { FailureBreakdownResponse } from '../api/types';

/** Derived «к разбору» numbers (variant B).
 *
 *  Single source of truth shared by the SummaryCells counter and the
 *  BreakdownSheet popover so the counter and the popover header can
 *  never drift apart.
 *
 *  «В счёте» (actionable): the operator should actually look at these.
 *    - serviceFailed — PPP didn't converge («не вычислено»).
 *    - other         — login / parse / edge cases («прочее»), strictly
 *                      the ``other`` category, NOT the old
 *                      ``other + uncategorised`` (that folded the whole
 *                      NULL bucket in → the 2326 bloat).
 *    - inCount       — serviceFailed + other. This is the headline
 *                      counter AND the popover header.
 *
 *  «Справочно · не в счёте» (shown muted, excluded from the count):
 *    - fileUnavailable / noReference / notApplicable — verbatim buckets.
 *    - failedUncat   — old FAILED jobs with no category yet. Recovered
 *                      from FAILED only (NOT the whole uncategorised
 *                      bucket, which also holds DONE/PENDING/SUBMITTED):
 *                        max(0, failed_total − service_failed
 *                               − file_unavailable − not_applicable
 *                               − other)
 *                      These will fold into real categories once the
 *                      backend backfill runs (a separate task).
 */
export interface ReviewBreakdown {
  serviceFailed: number;
  other: number;
  inCount: number;
  fileUnavailable: number;
  noReference: number;
  notApplicable: number;
  failedUncat: number;
}

export function computeReviewBreakdown(
  b: FailureBreakdownResponse | undefined,
): ReviewBreakdown {
  const serviceFailed = b?.service_failed ?? 0;
  const other = b?.other ?? 0;
  const fileUnavailable = b?.file_unavailable ?? 0;
  const noReference = b?.no_reference ?? 0;
  const notApplicable = b?.not_applicable ?? 0;
  const failedTotal = b?.failed_total ?? 0;

  const failedUncat = Math.max(
    0,
    failedTotal - serviceFailed - fileUnavailable - notApplicable - other,
  );

  return {
    serviceFailed,
    other,
    inCount: serviceFailed + other,
    fileUnavailable,
    noReference,
    notApplicable,
    failedUncat,
  };
}
